<template>
    <div class="alert-overlay" v-if="display" @click="handleClose">
      <div class="alert-container" @click.stop>
        <!-- Icon -->
        <div class="alert-icon" :class="`alert-icon-${type}`">
          <!-- Error Icon -->
          <svg v-if="type === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          
          <!-- Success Icon -->
          <svg v-else-if="type === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          
          <!-- Warning Icon -->
          <svg v-else-if="type === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          
          <!-- Info Icon -->
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </div>

        <!-- Title -->
        <h3 class="alert-title">{{ alertTitle }}</h3>

        <!-- Message -->
        <p class="alert-message">{{ alertMessage }}</p>

        <!-- Buttons -->
        <div class="alert-actions">
          <button 
            v-if="showCancel"
            class="alert-button alert-button-cancel" 
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
          <button 
            class="alert-button alert-button-confirm" 
            :class="`alert-button-${type}`"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
</template>

<script>
export default {
  name: 'AlertComponent',
  props: {
    type: {
      type: String,
      default: 'info', // 'error', 'success', 'warning', 'info'
      validator: (value) => ['error', 'success', 'warning', 'info'].includes(value)
    },
    confirmText: {
      type: String,
      default: 'OK'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      display: false,
      alertMessage: '',
      alertTitle: 'Alert'
    }
  },
  methods: {
    show(text, title = 'Alert') {
      this.alertMessage = text;
      this.alertTitle = title;
      this.display = true;
    },
    hide() {
      this.display = false;
    },
    handleConfirm() {
      this.$emit('confirm');
      this.hide();
    },
    handleCancel() {
      this.$emit('cancel');
      this.hide();
    },
    handleClose() {
      if (this.closeOnOverlay) {
        this.hide();
      }
    }
  }
}
</script>

<style scoped>
.alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.alert-container {
  background: rgba(10, 14, 38, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  padding: 32px 24px 24px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 212, 255, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.alert-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-icon svg {
  width: 36px;
  height: 36px;
}

.alert-icon-error {
  background: rgba(255, 59, 48, 0.15);
  border: 2px solid rgba(255, 59, 48, 0.4);
  color: #ff3b30;
}

.alert-icon-success {
  background: rgba(52, 199, 89, 0.15);
  border: 2px solid rgba(52, 199, 89, 0.4);
  color: #34c759;
}

.alert-icon-warning {
  background: rgba(255, 159, 10, 0.15);
  border: 2px solid rgba(255, 159, 10, 0.4);
  color: #ff9f0a;
}

.alert-icon-info {
  background: rgba(0, 212, 255, 0.15);
  border: 2px solid rgba(0, 212, 255, 0.4);
  color: #00d4ff;
}

.alert-title {
  color: #e0f7ff;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 12px 0;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
}

.alert-message {
  color: #94a3b8;
  font-size: 15px;
  text-align: center;
  margin: 0 0 28px 0;
  line-height: 1.5;
}

.alert-actions > * + * { margin-left: 14px; }

.alert-button {
  flex: 1;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.alert-button-cancel {
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #e0f7ff;
}

.alert-button-cancel:hover {
  background: rgba(30, 41, 59, 1);
  border-color: rgba(0, 212, 255, 0.5);
}

.alert-button-confirm {
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.alert-button-error {
  background: linear-gradient(135deg, #ff3b30, #d32f2f);
}

.alert-button-error:hover {
  box-shadow: 0 6px 20px rgba(255, 59, 48, 0.4);
}

.alert-button-success {
  background: linear-gradient(135deg, #34c759, #28a745);
}

.alert-button-success:hover {
  box-shadow: 0 6px 20px rgba(52, 199, 89, 0.4);
}

.alert-button-warning {
  background: linear-gradient(135deg, #ff9f0a, #f57c00);
}

.alert-button-warning:hover {
  box-shadow: 0 6px 20px rgba(255, 159, 10, 0.4);
}

.alert-button-info {
  background: linear-gradient(135deg, #00d4ff, #0099cc);
}

.alert-button-info:hover {
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
}

@media (max-width: 480px) {
  .alert-container {
    padding: 28px 20px 20px;
  }

  .alert-title {
    font-size: 18px;
  }

  .alert-message {
    font-size: 14px;
  }

  .alert-actions {
    flex-direction: column;
  }
}
</style>